import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import {
  UserCircleIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { customerService, userService } from '@/services/firebase'
import { Customer } from '@/types'
import { BAHAMAS_ISLANDS } from '@/config/company'
import LoadingSpinner from '@/components/LoadingSpinner'

interface AddressForm {
  street: string
  city: string
  state: string
  zipCode: string
  country: string
}

const EMPTY_ADDRESS: AddressForm = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'Bahamas',
}

export default function Profile() {
  const { user } = useAuth()
  const isCustomer = user?.role === 'customer'

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState<AddressForm>(EMPTY_ADDRESS)

  useEffect(() => {
    if (!user) return

    const load = async () => {
      setLoading(true)
      setLoadError(null)
      try {
        setName(user.name || '')

        if (isCustomer) {
          const customer: Customer | null = await customerService.getCustomerById(user.id)
          setPhone(customer?.phone || '')
          setAddress({ ...EMPTY_ADDRESS, ...(customer?.address || {}) })
        }
      } catch (err) {
        console.error('Error loading profile:', err)
        setLoadError("We couldn't load your profile. Check your connection and try again.")
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [user, isCustomer])

  const hasDeliveryAddress = Boolean(address.street.trim() && address.city.trim())

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    if (!name.trim()) {
      toast.error('Please enter your name.')
      return
    }

    setSaving(true)
    try {
      await userService.updateUser(user.id, { name: name.trim() })

      if (isCustomer) {
        await customerService.updateCustomer(user.id, {
          name: name.trim(),
          phone: phone.trim(),
          address: {
            street: address.street.trim(),
            city: address.city.trim(),
            state: address.state.trim(),
            zipCode: address.zipCode.trim(),
            country: address.country.trim() || 'Bahamas',
          },
        })
      }

      toast.success('Profile saved.')
    } catch (err) {
      console.error('Error saving profile:', err)
      toast.error('Could not save your profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
        <p className="mt-3 text-red-700">{loadError}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  const inputClass =
    'block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm'
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1'

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        <p className="mt-1 text-gray-600">
          {isCustomer
            ? 'Your details and delivery address. We use these to fill in your shipments automatically.'
            : 'Your account details.'}
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* ---------------- Account ---------------- */}
        <section className="rounded-lg bg-white p-6 shadow">
          <div className="mb-5 flex items-center gap-2">
            <UserCircleIcon className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Account</h2>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className={labelClass}>
                Full name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="email" className={labelClass}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className={`${inputClass} bg-gray-50 text-gray-500`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Contact us if you need to change your email.
              </p>
            </div>

            {isCustomer && (
              <div>
                <label htmlFor="phone" className={labelClass}>
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClass}
                  placeholder="(242) 000-0000"
                />
              </div>
            )}

            {!isCustomer && (
              <div>
                <label className={labelClass}>Role</label>
                <input
                  type="text"
                  value={user?.role || ''}
                  disabled
                  className={`${inputClass} bg-gray-50 capitalize text-gray-500`}
                />
              </div>
            )}
          </div>
        </section>

        {/* ---------------- Delivery address (customers only) ---------------- */}
        {isCustomer && (
          <section className="rounded-lg bg-white p-6 shadow">
            <div className="mb-2 flex items-center gap-2">
              <MapPinIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Delivery address</h2>
              {hasDeliveryAddress && (
                <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  Saved
                </span>
              )}
            </div>
            <p className="mb-5 text-sm text-gray-600">
              Where your packages get delivered. Saving it here means you won&apos;t have to type
              it again — every new shipment fills it in for you.
            </p>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="street" className={labelClass}>
                  Street address
                </label>
                <input
                  id="street"
                  type="text"
                  value={address.street}
                  onChange={(e) => setAddress({ ...address, street: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. 12 Bay Street"
                />
              </div>

              <div>
                <label htmlFor="city" className={labelClass}>
                  Town / settlement
                </label>
                <input
                  id="city"
                  type="text"
                  value={address.city}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Nassau"
                />
              </div>

              <div>
                <label htmlFor="island" className={labelClass}>
                  Island
                </label>
                <select
                  id="island"
                  value={address.state}
                  onChange={(e) => setAddress({ ...address, state: e.target.value })}
                  className={inputClass}
                >
                  <option value="">Select an island</option>
                  {BAHAMAS_ISLANDS.map((island) => (
                    <option key={island} value={island}>
                      {island}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="zip" className={labelClass}>
                  Postal code <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  id="zip"
                  type="text"
                  value={address.zipCode}
                  onChange={(e) => setAddress({ ...address, zipCode: e.target.value })}
                  className={inputClass}
                  placeholder="P.O. Box N-0000"
                />
              </div>

              <div>
                <label htmlFor="country" className={labelClass}>
                  Country
                </label>
                <input
                  id="country"
                  type="text"
                  value={address.country}
                  onChange={(e) => setAddress({ ...address, country: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          </section>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}
